import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  type AlertColor,
  ButtonBase,
  type ButtonBaseProps,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Warning2 } from 'iconsax-react';
import type { ElementType, FC, ReactNode } from 'react';

export type BannerProps = {
  severity: AlertColor;
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  action?: Omit<ButtonBaseProps<ElementType>, 'sx'>;
  onClose?: () => void;
  sx?: SxProps<Theme>;
};

type SeverityStyle = {
  backgroundColor: string;
  borderColor: string;
  icon: ReactNode;
  iconColor: string;
  titleColor: string;
  bodyColor: string;
};

const getSeverityStyles = (theme: Theme): { [key in AlertColor]: SeverityStyle } => ({
  info: {
    backgroundColor: theme.palette.SlabBlue.IceLight,
    borderColor: theme.palette.SlabGray.Stroke,
    icon: <Warning2 size={24} />,
    iconColor: theme.palette.SlabIndigo[500],
    titleColor: theme.palette.text.primary,
    bodyColor: theme.palette.SlabGray[600],
  },
  warning: {
    backgroundColor: theme.palette.SlabYellow[100],
    borderColor: 'transparent',
    icon: <Warning2 size={24} />,
    iconColor: theme.palette.SlabYellow[800],
    titleColor: theme.palette.text.primary,
    bodyColor: theme.palette.SlabGray[600],
  },
  error: {
    backgroundColor: theme.palette.SlabRed[100],
    borderColor: 'transparent',
    icon: <Warning2 size={24} />,
    iconColor: theme.palette.SlabRed[500],
    titleColor: theme.palette.text.primary,
    bodyColor: theme.palette.SlabGray[600],
  },
  success: {
    backgroundColor: theme.palette.SlabGreen[100],
    borderColor: 'transparent',
    icon: <CheckCircleOutlineIcon />,
    iconColor: theme.palette.SlabGreen[600],
    titleColor: theme.palette.text.primary,
    bodyColor: theme.palette.SlabGray[600],
  },
});

export const Banner: FC<BannerProps> = ({
  severity,
  title,
  children,
  icon,
  action,
  onClose,
  sx,
}) => {
  const theme = useTheme();
  const {
    backgroundColor,
    borderColor,
    icon: defaultIcon,
    iconColor,
    titleColor,
    bodyColor,
  } = getSeverityStyles(theme)[severity];

  // MUI's `action` replaces its close button, and a banner can have both.
  return (
    <Alert
      severity={severity}
      icon={icon ?? defaultIcon}
      action={
        action || onClose ? (
          <Stack direction='row' sx={{ alignItems: 'center', gap: 1 }}>
            {action && (
              <ButtonBase
                disableRipple
                {...action}
                sx={{
                  typography: 'h6',
                  fontSize: '18px',
                  fontWeight: '400',
                  color: iconColor,
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                  '&.Mui-focusVisible': { outline: `2px solid ${iconColor}`, outlineOffset: '2px' },
                }}
              />
            )}
            {onClose && (
              <IconButton size='small' onClick={onClose} aria-label='Close'>
                <CloseIcon sx={{ width: '18px', height: '18px', color: bodyColor }} />
              </IconButton>
            )}
          </Stack>
        ) : undefined
      }
      sx={[
        {
          p: 2,
          borderRadius: '8px',
          border: `1px solid ${borderColor}`,
          backgroundColor,
          '& .MuiAlert-icon': { p: 0, mr: 1, opacity: 1, fontSize: '24px', color: iconColor },
          '& .MuiAlert-message': { p: 0, flex: 1, minWidth: 0 },
          '& .MuiAlert-action': { p: 0, my: '-2px', ml: 2, mr: 0, alignItems: 'flex-start' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack sx={{ gap: 0.5 }}>
        {title ? (
          <Typography
            variant='h6'
            sx={{ fontWeight: '500', fontSize: '18px', lineHeight: '24px', color: titleColor }}
          >
            {title}
          </Typography>
        ) : undefined}
        <Typography
          variant='body3'
          component='div'
          sx={{
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '24px',
            color: title ? bodyColor : theme.palette.SlabGray[900],
          }}
        >
          {children}
        </Typography>
      </Stack>
    </Alert>
  );
};
